import clsx from 'clsx';
import { useEditorRef, useEditorSelector } from 'platejs/react';
import { useDropdown } from '../../hooks/useDropdown';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import styles from './FontSizeMenu.module.scss';
import toolbarStyles from '../PlateToolbar/PlateToolbar.module.scss';
import { applyMarkFast } from '../applyMarkFast';
import { BODY_DEFAULT_FONT_SIZE_PX } from '../../bodyFontSize';

// Presets cover the editor body default (15px), every heading intrinsic
// (h3=20, h2=24, h1=30 — see BLOCK_INTRINSIC_PX) and the common document body
// sizes the generated templates use (incl. the legacy 16px body), so a user
// can always reselect the exact size of generated content to keep new edits
// uniform with it. Keep this list sorted ascending.
const PRESET_SIZES = [
    '10px',
    '11px',
    '12px',
    '13px',
    '14px',
    '15px',
    '16px',
    '18px',
    '20px',
    '22px',
    '24px',
    '26px',
    '28px',
    '30px',
    '32px',
    '36px',
    '40px',
    '48px',
];
// Body default — matches `.content { font-size: 15px }` in PlateEditor.module.scss.
// If you change this, change the CSS at the same time and vice versa.
const DEFAULT_SIZE = BODY_DEFAULT_FONT_SIZE_PX;
const STEP = 2;
const MIN = 8;
const MAX = 96;

// Intrinsic font sizes for block types that come with their own CSS size.
// When a leaf has no explicit fontSize mark and the caret is inside one of
// these blocks, the rendered text reflects the block's CSS — surface that
// in the toolbar so the displayed number matches what the user actually
// sees on screen. Sourced from PlateEditor.module.scss.
const BLOCK_INTRINSIC_PX: Record<string, number> = {
    h1: 30,
    h2: 24,
    h3: 20,
};

export function parseSize(size: string | number | undefined): number {
    if (size == null || size === '') return DEFAULT_SIZE;
    const n = typeof size === 'number' ? size : parseFloat(size);
    return Number.isFinite(n) ? n : DEFAULT_SIZE;
}

export function clampFontSize(size: number): number {
    return Math.max(MIN, Math.min(MAX, size));
}

export function FontSizeMenu() {
    const editor = useEditorRef();
    const { open, setOpen, ref: wrapRef } = useDropdown();

    // Effective font size resolution — falls through layers until something
    // resolves to a concrete pixel value the toolbar can display:
    //   1. Leaf at the caret has an explicit `fontSize` mark   → use it.
    //   2. `editor.api.marks()` returns one                    → use it.
    //      (this is the "edge merged" marks Plate computes for the next
    //       insertion; sometimes populated when the leaf lookup isn't.)
    //   3. Caret is inside a heading block (h1/h2/h3)          → use the
    //      block's intrinsic CSS size.
    //   4. Fall back to the body default (16px).
    //
    // Previous implementation only read (2), so legacy docs with leaf-level
    // fontSize marks (e.g. "Data Not Available" leaves at 14px) showed 16
    // and clicking "16" actually *increased* the rendered font.
    const numeric: number = useEditorSelector((ed: any) => {
        try {
            // (1) Explicit leaf mark
            const leafEntry = ed?.api?.leaf?.();
            const leafNode = Array.isArray(leafEntry) ? leafEntry[0] : leafEntry;
            if (leafNode?.fontSize) return parseSize(leafNode.fontSize);
            // (2) Edge-merged marks (fallback for collapsed selection at leaf boundaries)
            const marks = ed?.api?.marks?.();
            if (marks?.fontSize) return parseSize(marks.fontSize);
            // (3) Block intrinsic size for headings
            const blockType = ed?.api?.block?.({ block: true })?.[0]?.type as string | undefined;
            if (blockType && BLOCK_INTRINSIC_PX[blockType] != null) return BLOCK_INTRINSIC_PX[blockType];
        } catch {
            /* fall through */
        }
        return DEFAULT_SIZE;
    }, []);

    const apply = (size: number) => {
        const clamped = clampFontSize(size);
        const ed: any = editor;
        // Single-pass apply — see applyMarkFast for the rationale.
        applyMarkFast(ed, 'fontSize', `${clamped}px`, (v) => ed?.tf?.fontSize?.addMark?.(v));
    };

    const decr = (e: React.MouseEvent) => {
        e.preventDefault();
        apply(numeric - STEP);
    };
    const incr = (e: React.MouseEvent) => {
        e.preventDefault();
        apply(numeric + STEP);
    };
    const pick = (size: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        apply(parseSize(size));
        setOpen(false);
    };

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <div className={styles.stepper}>
                <button
                    type="button"
                    className={styles.stepBtn}
                    onMouseDown={decr}
                    title="Decrease font size"
                    aria-label="Decrease font size"
                >
                    <RemoveRoundedIcon fontSize="small" />
                </button>
                <button
                    type="button"
                    className={clsx(styles.size, open && styles.sizeOpen)}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        setOpen((o) => !o);
                    }}
                    aria-haspopup="true"
                    aria-expanded={open}
                    aria-label="Font size presets"
                >
                    {numeric}
                    <ArrowDropDownRoundedIcon className={styles.caret} fontSize="small" />
                </button>
                <button
                    type="button"
                    className={styles.stepBtn}
                    onMouseDown={incr}
                    title="Increase font size"
                    aria-label="Increase font size"
                >
                    <AddRoundedIcon fontSize="small" />
                </button>
            </div>
            {open && (
                <div className={styles.popover} role="menu" aria-label="Pick font size">
                    {PRESET_SIZES.map((size) => {
                        const n = parseSize(size);
                        return (
                            <button
                                key={size}
                                type="button"
                                className={clsx(styles.item, numeric === n && styles.itemActive)}
                                role="menuitem"
                                onMouseDown={pick(size)}
                                style={{ fontSize: size }}
                            >
                                {n}
                            </button>
                        );
                    })}
                    <form
                        className={styles.customRow}
                        onSubmit={(e) => {
                            e.preventDefault();
                            const input = e.currentTarget.elements.namedItem('customSize') as HTMLInputElement | null;
                            const n = input ? parseFloat(input.value) : NaN;
                            if (Number.isFinite(n)) {
                                apply(n);
                                setOpen(false);
                            }
                        }}
                    >
                        <input
                            type="number"
                            name="customSize"
                            min={MIN}
                            max={MAX}
                            step={1}
                            defaultValue={numeric}
                            className={styles.customInput}
                            aria-label="Custom font size"
                            // Submit via Enter is the form's natural behavior. Blur also commits.
                            onBlur={(e) => {
                                const n = parseFloat(e.currentTarget.value);
                                if (Number.isFinite(n) && n !== numeric) apply(n);
                            }}
                        />
                        <span className={styles.customUnit}>px</span>
                    </form>
                </div>
            )}
        </div>
    );
}
